<template>
  <div class="flex flex-wrap gap-2 mb-2">
    <button class="btn btn-secondary btn-sm" :disabled="buttonsDisabled" @click="shuffle">
      <i class="fas fa-random mr-1"></i>{{ t('textboxbuttons.Shuffle') }}
    </button>

    <button class="btn btn-secondary btn-sm" :disabled="buttonsDisabled" @click="sort">
      <i class="fas fa-sort-alpha-up mr-1"></i>{{ t('textboxbuttons.Sort') }}
    </button>

    <div class="relative btn btn-secondary btn-sm">
      <input type="file" accept="image/*" multiple :disabled="buttonsDisabled" @change="handleFileUpload"
        class="absolute inset-0 w-full h-full opacity-0" />
      <button class="pointer-events-none" :disabled="buttonsDisabled">
        <i class="far fa-image mr-1"></i>{{ t('textboxbuttons.Add image') }}
      </button>
    </div>

    <label v-if="displayAdvancedCheckbox" class="flex items-center space-x-2 cursor-pointer"
      :class="{ 'opacity-50 cursor-not-allowed': buttonsDisabled }">
      <input type="checkbox" v-model="wheelIsAdvanced" :disabled="buttonsDisabled" :key="updateAdvancedCheckbox"
        class="h-4 w-4 text-primary-500 focus:ring-primary-500 border-stone-300 rounded">
      <span class="text-sm text-stone-800 dark:text-stone-200">
        {{ t('textboxbuttons.Advanced') }}
      </span>
    </label>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
import { useWheelStore } from '@/stores/useWheelStore';
import * as Util from '@utils/Util.js';
import * as ImageUtil from '@utils/ImageUtil.js';

const { t } = useI18n();
const wheelStore = useWheelStore();

const updateAdvancedCheckbox = ref(0);
const sheetLinked = computed(() => wheelStore.sheetLinked);
const wheelIsBusy = computed(() => wheelStore.wheelIsBusy);
const buttonsDisabled = computed(() => sheetLinked.value || wheelIsBusy.value);

const wheelIsAdvanced = computed({
  get() {
    return wheelStore.wheelIsAdvanced;
  },
  async set(newValue) {
    if (!newValue) {
      const confirmed = await window.$ui.dialog.confirm({
        title: t('textboxbuttons.Revert from advanced'),
        message: t('textboxbuttons.Reverting will reset'),
        cancelText: t('common.Cancel'),
        confirmText: t('common.Continue')
      });
      if (confirmed) {
        await wheelStore.setAdvanced(newValue);
        Util.trackEvent('Wheel', 'RevertFromAdvanced');
      } else {
        updateAdvancedCheckbox.value++;
      }
    } else {
      await wheelStore.setAdvanced(newValue);
      Util.trackEvent('Wheel', 'ConvertToAdvanced');
    }
  }
});

const displayAdvancedCheckbox = computed(() => !Util.browserIsIE());

function shuffle() {
  Util.trackEvent('Wheel', 'ShuffleEntries', '');
  wheelStore.shuffleEntries();
}
function sort() {
  Util.trackEvent('Wheel', 'SortEntries', '');
  wheelStore.sortEntries();
}
function handleFileUpload(event) {
  const files = event.target.files;
  if (!files || files.length === 0) return;

  Util.trackEvent('Wheel', 'UploadPieSliceImage', files.length);
  for (const file of files) {
    const reader = new FileReader();
    reader.onload = async function (e) {
      const dataUri = await ImageUtil.optimizeSliceImage(e.target.result);
      wheelStore.appendImageEntry(dataUri);
    };
    reader.readAsDataURL(file);
  }
  // Reset the input field
  event.target.value = '';
}
</script>
<script>
// Options API block removed in favor of <script setup>
</script>
